import HubLeadCTA from "@/components/HubLeadCTA";
import { ctaDesc, ctaTitle } from "@/lib/seo";
import { RelatedHubGrid } from "@/components/RelatedHubGrid";
import { createFileRoute, Link } from "@tanstack/react-router";
import { APOSTILLE_PRODUCTS, APOSTILLE_PRODUCT_CATEGORIES, APOSTILLE_CATALOG_PRICE_MIN, APOSTILLE_CATALOG_PRICE_MAX, APOSTILLE_TURNAROUND_STD } from "@/data/apostille-products";
import { BRAND_URL } from "@/lib/brand";
import { ArrowRight, Globe2, ShieldCheck, Clock } from "lucide-react";
import { ProductCatalogHubNav } from "@/components/ProductCatalogHubNav";

const PATH = "/en/apostille/products";
const URL = `${BRAND_URL}${PATH}`;

export const Route = createFileRoute("/en/apostille/products/")({
  head: () => ({
    meta: [
      { title: ctaTitle("Apostille & MFA Legalisation — Bangkok Catalog | NYC", "/en/apostille/products/index", "en")},
      { name: "description", content: ctaDesc("Full Apostille & MFA catalog: translation + notary + MFA + destination Apostille. Ask us anything — real experts reply within minutes.", "/en/apostille/products/index", "en")},
      { property: "og:title", content: ctaTitle("Apostille & MFA Legalisation Product Catalog | NYC", "/en/apostille/products/index", "en")},
      { property: "og:description", content: ctaDesc("Certified translation + notary + MFA + destination Apostille in one office. 125+ Hague destinations.", "/en/apostille/products/index", "en")},
      { property: "og:url", content: URL },
      { property: "og:locale", content: "en_US" },
    ],
    links: [
      { rel: "canonical", href: URL },
      { rel: "alternate", hrefLang: "en", href: URL },
      { rel: "alternate", hrefLang: "th-TH", href: `${BRAND_URL}/apostille/products` },
      { rel: "alternate", hrefLang: "x-default", href: `${BRAND_URL}/apostille/products` },
    ],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify({
        "@context": "https://schema.org", "@type": "ItemList",
        "@id": `${URL}#catalog`,
        name: "Apostille & MFA Product Catalog",
        numberOfItems: APOSTILLE_PRODUCTS.length,
        itemListElement: APOSTILLE_PRODUCTS.map((p, i) => ({
          "@type": "ListItem", position: i + 1,
          url: `${BRAND_URL}/en/apostille/products/${p.slug}`,
          name: p.nameEn,
        })),
      })},
    ],
  }),
  component: () => (
    <main className="min-h-screen bg-background pb-20">
      <section className="mx-auto max-w-6xl px-4 pt-10">
        <div className="flex flex-wrap gap-2 text-[11px] font-bold uppercase tracking-widest text-blue-700">
          <span className="rounded-full bg-blue-100 px-3 py-1">Apostille · MFA · Hague</span>
        </div>
        <h1 className="mt-3 font-display text-4xl font-extrabold text-navy-deep sm:text-5xl">
          Apostille & MFA Legalisation Services
        </h1>
        <p className="mt-3 max-w-3xl text-lg text-slate-600">
          {APOSTILLE_PRODUCTS.length} SKUs covering both outbound (Thai → Hague) and inbound (foreign → Thailand) documents — 
          translation + notary + MFA + destination Apostille <strong>all under one roof</strong>. 125+ Hague Convention members supported including China (Nov 2023).
        </p>

        <div className="mt-6 grid gap-3 sm:grid-cols-3">
          <div className="rounded-xl border border-emerald-200 bg-emerald-50/40 p-4">
            <ShieldCheck className="mb-2 h-5 w-5 text-emerald-700" />
            <div className="font-display font-bold text-emerald-900">Thai-Bar Notarial Attorney</div>
            <div className="text-xs text-slate-600">Registered with Lawyers Council of Thailand</div>
          </div>
          <div className="rounded-xl border border-blue-200 bg-blue-50/40 p-4">
            <Clock className="mb-2 h-5 w-5 text-blue-700" />
            <div className="font-display font-bold text-blue-900">{APOSTILLE_TURNAROUND_STD}</div>
            <div className="text-xs text-slate-600">Standard · 3-day express available</div>
          </div>
          <div className="rounded-xl border border-slate-200 bg-slate-50/40 p-4">
            <Globe2 className="mb-2 h-5 w-5 text-slate-700" />
            <div className="font-display font-bold text-slate-900">130 countries</div>
            <div className="text-xs text-slate-600">Hague members + non-Hague chain</div>
          </div>
        </div>

        <div className="mt-6 flex flex-wrap gap-2 text-xs">
          {APOSTILLE_PRODUCT_CATEGORIES.map((c) => (
            <a key={c.id} href={`#cat-${c.id}`} className="rounded-full border border-blue-200 bg-white px-3 py-1.5 font-semibold text-blue-800 hover:bg-blue-50">
              {c.labelEn} <span className="opacity-60">({c.count})</span>
            </a>
          ))}
        </div>
      </section>

      {APOSTILLE_PRODUCT_CATEGORIES.map((c) => {
        const items = APOSTILLE_PRODUCTS.filter((p) => p.category === c.id);
        return (
          <section key={c.id} id={`cat-${c.id}`} className="mx-auto mt-12 max-w-6xl px-4">
            <h2 className="font-display text-2xl font-extrabold text-navy-deep">{c.labelEn}</h2>
            <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {items.map((p) => (
                <Link
                  key={p.sku}
                  to="/en/apostille/products/$slug"
                  params={{ slug: p.slug }}
                  className="group rounded-2xl border border-slate-200 bg-white p-5 transition hover:-translate-y-0.5 hover:border-blue-400 hover:shadow-md"
                >
                  <div className="text-[10px] font-bold uppercase tracking-widest text-blue-700">{p.categoryLabelEn}</div>
                  <div className="mt-1 font-display text-lg font-extrabold text-navy-deep group-hover:text-blue-700">{p.nameEn}</div>
                  <div className="mt-1 text-xs text-slate-500">{p.nameTh}</div>
                  <div className="mt-3 font-display text-lg font-extrabold text-emerald-800">
                    THB {p.priceMin.toLocaleString()}–{p.priceMax.toLocaleString()}
                  </div>
                  <div className="text-xs text-slate-500">{p.turnaround}</div>
                  <div className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-blue-700">
                    View details <ArrowRight className="h-3 w-3" />
                  </div>
                </Link>
              ))}
            </div>
          </section>
        );
      })}
      <ProductCatalogHubNav locale="en" current="apostille" />
    <RelatedHubGrid locale="en" /><HubLeadCTA locale="en" service="Apostille" />
      </main>
  ),
});
